import './Filters.scss';
import { Typography } from '@design-system/components/Typography/Typography';
import { Button, Checkbox, Icon } from '@design-system/index';
import { Delete, Filtres } from '@icons';
import { useFilters } from './useFilters';
import { ActiveFilter, Filter, FilterComparator } from '@utils/filters';
import { FILTER_COMPARATOR } from '@utils/enumTranslation';

type FiltersProps = {
    onFiltersChange?: (filters: ActiveFilter[]) => void;
    filters: Filter[];
};

export const Filters = ({
    onFiltersChange,
    filters
}: FiltersProps): JSX.Element => {

    const { activeFilters, addFilter, removeFilter, clearFilters, sendFilters, isAddFilterModalOpen, setIsAddFilterModalOpen, ref, sentFilters } = useFilters(onFiltersChange);

    return (
        <div className="filters-container">
            <Typography intrinsic="p" variant="p2" color="grey1">
                Filtrer par :
            </Typography>
            <Button
                size="small"
                color="grey"
                bubble={sentFilters.length}
                icon={<Icon icon={<Filtres />} size={16} color="grey1" />}
                bordered
                onClick={() => setIsAddFilterModalOpen((!isAddFilterModalOpen))}
            />

            {isAddFilterModalOpen && (
                <div ref={ref} className="filters-modal">

                    <Typography intrinsic="p" variant="sub1" color="secondary">Types de filtres</Typography>

                    {/* Afficher tous les filtres dans l'ordre d'origine */}
                    <div className="filters-list">
                        {filters.filter(filter => filter.display !== false).map(filter => {
                            const activeFilter = activeFilters.find(af => af.id === filter.id);
                            return (
                                <div key={filter.id} className="filter-item">
                                    <div className="filter-item-header"
                                        onClick={() => 
                                            activeFilter 
                                            ? removeFilter(filter.id)
                                            : addFilter(
                                                filter.id,
                                                filter.type === 'boolean' ? true : filter.type === 'select' ? (filter.options?.[0]?.value || '') : filter.type === 'multiSelect' ? [] : '',
                                                filter.defaultComparator || (filter.comparatorTypes && filter.comparatorTypes.length > 0 ? filter.comparatorTypes[0] : undefined)
                                            )
                                        }
                                    >
                                        <Typography intrinsic="p" variant="p2" color="primary">{filter.label}</Typography>
                                        <Button
                                            variant="icon-hover"
                                            color="grey-primary"
                                            size="small"
                                            className={!activeFilter ? 'rotate-45' : ''}
                                            icon={<Icon icon={<Delete />} size={14} color="grey1" />}
                                        />
                                    
                                    </div>
                                    {activeFilter && (
                                        <div className="filter-item-content">
                                            {/* Sélecteur de comparateur si applicable */}
                                            {filter.comparatorTypes && (
                                                <select
                                                    onChange={e => addFilter(filter.id, activeFilter.value, e.target.value as FilterComparator)}
                                                    value={activeFilter.comparator || ''}
                                                >
                                                    {filter.comparatorTypes.map(type => (
                                                        <option key={type} value={type}>{FILTER_COMPARATOR[type]}</option>
                                                    ))}
                                                </select>
                                            )}
                                            {/* Champs selon le type */}
                                            {filter.type === 'date' && (
                                                <div className="date-inputs">
                                                    {activeFilter.comparator === 'isBetween' ? (
                                                        <div className="inputs">
                                                            <input
                                                                type="date"
                                                                placeholder="Date début"
                                                                onChange={e => {
                                                                    const currentValue = Array.isArray(activeFilter.value) ? activeFilter.value : ['', ''];
                                                                    addFilter(filter.id, [e.target.value, currentValue[1]], 'isBetween');
                                                                }}
                                                                value={Array.isArray(activeFilter.value) ? activeFilter.value[0] : ''}
                                                            />
                                                            <input
                                                                type="date"
                                                                placeholder="Date fin"
                                                                onChange={e => {
                                                                    const currentValue = Array.isArray(activeFilter.value) ? activeFilter.value : ['', ''];
                                                                    addFilter(filter.id, [currentValue[0], e.target.value], 'isBetween');
                                                                }}
                                                                value={Array.isArray(activeFilter.value) ? activeFilter.value[1] : ''}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <input
                                                            type="date"
                                                            onChange={e => addFilter(filter.id, e.target.value, activeFilter.comparator || ('' as FilterComparator))}
                                                            value={!Array.isArray(activeFilter.value) ? (activeFilter.value as string) || '' : ''}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            {filter.type === 'string' && (
                                                <div className="string-inputs">
                                                    <input
                                                        type="text"
                                                        onChange={e => addFilter(filter.id, e.target.value, activeFilter.comparator || ('' as FilterComparator))}
                                                        value={!Array.isArray(activeFilter.value) ? (activeFilter.value as string) || '' : ''}
                                                    />
                                                </div>
                                            )}
                                            {filter.type === 'select' && (
                                                <div className="select-inputs">
                                                    <select onChange={e => addFilter(filter.id, e.target.value, 'equals')} value={activeFilter.value as string || ''}>
                                                        {filter.options?.map(option => (
                                                            <option key={option.value} value={option.value}>{option.label}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                            {filter.type === 'multiSelect' && (
                                                <div className="multi-select-inputs">
                                                    {filter.options?.map(option => {
                                                        const currentValues = (activeFilter.value as string[] || []);
                                                        const isChecked = currentValues.includes(option.value);
                                                        
                                                        return (
                                                            <Checkbox
                                                                checked={isChecked}
                                                                onChange={() => {
                                                                    const newValues = isChecked 
                                                                        ? currentValues.filter(value => value !== option.value)
                                                                        : [...currentValues, option.value];
                                                                    addFilter(filter.id, newValues, 'includes');
                                                                }}
                                                            >
                                                                <Typography intrinsic="p" variant="p2" color="secondary">
                                                                    {option.label}
                                                                </Typography>
                                                            </Checkbox>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            {filter.type === 'boolean' && (
                                                <div className="boolean-inputs">
                                                    <select onChange={e => addFilter(filter.id, e.target.value === 'true', 'equals')} value={String(activeFilter.value) || 'true'}>
                                                        <option value="true">Oui</option>
                                                        <option value="false">Non</option>
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Actions globales */}
                    {(activeFilters.length > 0 || sentFilters.length > 0) &&
                        <div className="filters-actions">
                            {sentFilters.length > 0 && <>
                                <Button variant="secondary" onClick={clearFilters}>
                                    Réinitialiser les filtres   
                                </Button>
                            </>}
                            <Button variant="primary" onClick={sendFilters}>Valider</Button>
                        </div>
                    }
                </div>
            )}
        </div>
    );
};
